import { type GoalState } from "../buildRequestTemplate";

/** Mirrors the server's ``_TRIAGE_LABEL_PATTERN``. Labels become Roboto
 *  tags and Bedrock-tool enum values, so the character set is deliberately
 *  conservative. */
export const TRIAGE_LABEL_PATTERN = /^[a-zA-Z0-9_-]+$/;
/** Mirror of ``_MAX_TRIAGE_LABELS`` on the backend. */
export const MAX_TRIAGE_LABELS = 50;
/** Mirror of ``_MAX_TRIAGE_DESCRIPTION_CHARS`` on the backend. */
export const MAX_TRIAGE_DESCRIPTION_CHARS = 500;
/** Mirror of ``DatasetSummaryAgentGoal.summary_format_spec_prompt`` upper bound. */
export const MAX_SUMMARY_FORMAT_CHARS = 4000;

export interface TriageRowError {
  /** Index into ``label_vocabulary``. ``-1`` means the error is goal-level
   *  (e.g. "at least one entry required") rather than tied to a row. */
  index: number;
  /** Which field on the row failed. ``"row"`` for goal-level errors. */
  field: "label" | "description" | "row";
  message: string;
}

export interface SummaryGoalErrors {
  /** Validation messages for the summary-format textarea. Empty if valid. */
  summary_format_spec_prompt: string[];
}

export interface TriageGoalErrors {
  /** Per-row + goal-level errors. Empty if valid. */
  rows: TriageRowError[];
}

/** Pure validator for a single goal. The returned union is discriminated
 *  by the presence of ``summary_format_spec_prompt`` vs. ``rows``, mirroring
 *  the two ``GoalState`` variants. */
export function validateGoal(
  goal: GoalState,
): SummaryGoalErrors | TriageGoalErrors {
  if (goal.goal_type === "dataset_summary") {
    return validateSummaryGoal(goal);
  }
  return validateTriageGoal(goal);
}

function validateSummaryGoal(goal: {
  summary_format_spec_prompt: string;
}): SummaryGoalErrors {
  const errors: string[] = [];
  // Cap is enforced on raw length to mirror the server's pydantic
  // ``max_length=4000`` (which counts whitespace). Whitespace-only input is
  // treated as "use default" elsewhere, so we don't flag it as required here,
  // but we still reject overflow regardless of trim state.
  if (goal.summary_format_spec_prompt.length > MAX_SUMMARY_FORMAT_CHARS) {
    errors.push(
      `Summary format hint must be at most ${MAX_SUMMARY_FORMAT_CHARS} characters.`,
    );
  }
  return { summary_format_spec_prompt: errors };
}

function validateTriageGoal(goal: {
  label_vocabulary: Array<{ label: string; description: string }>;
}): TriageGoalErrors {
  const rows: TriageRowError[] = [];
  if (goal.label_vocabulary.length === 0) {
    rows.push({
      index: -1,
      field: "row",
      message: "Add at least one label.",
    });
    return { rows };
  }
  if (goal.label_vocabulary.length > MAX_TRIAGE_LABELS) {
    rows.push({
      index: -1,
      field: "row",
      message: `At most ${MAX_TRIAGE_LABELS} labels per goal.`,
    });
  }
  const seenLabels = new Map<string, number>();
  goal.label_vocabulary.forEach((row, idx) => {
    if (row.label.trim() === "") {
      rows.push({
        index: idx,
        field: "label",
        message: "Label is required.",
      });
    } else if (!TRIAGE_LABEL_PATTERN.test(row.label)) {
      rows.push({
        index: idx,
        field: "label",
        message:
          "Use only letters, digits, underscores, or hyphens (no spaces).",
      });
    } else {
      const previousIdx = seenLabels.get(row.label);
      if (previousIdx !== undefined) {
        rows.push({
          index: idx,
          field: "label",
          message: `Duplicate of label at row ${previousIdx + 1}.`,
        });
      } else {
        seenLabels.set(row.label, idx);
      }
    }
    if (row.description.trim() === "") {
      rows.push({
        index: idx,
        field: "description",
        message: "Describe what this label signifies.",
      });
    } else if (row.description.length > MAX_TRIAGE_DESCRIPTION_CHARS) {
      rows.push({
        index: idx,
        field: "description",
        message: `Description must be at most ${MAX_TRIAGE_DESCRIPTION_CHARS} characters.`,
      });
    }
  });
  return { rows };
}

/** Convenience: true when no validator on any goal would block a save.
 *  Note: returns ``true`` for an empty array — the server requires at least
 *  one goal, so use ``canSubmit`` elsewhere to enforce ``>=1``. */
export function allGoalsValid(goals: GoalState[]): boolean {
  return goals.every((goal) => {
    const errors = validateGoal(goal);
    if ("summary_format_spec_prompt" in errors) {
      return errors.summary_format_spec_prompt.length === 0;
    }
    return errors.rows.length === 0;
  });
}
